import { useTranslation } from 'react-i18next';
import {
  CompaniesLabel,
  Container,
  NoContentLabel,
  AddWrapper,
  AddLabel,
  RowWrapper,
  InputWrapper,
  FirstRow,
  LeftSide,
  RightSide,
  ToggleGroupWrapper,
  ToggleLabel,
  NoContentLabelWrapper,
} from './ModelsCompanies.styled';
import Input from 'components/Input/Input';
import { useEffect, useState } from 'react';
import { MODELS_COMPANIES_PER_PAGE } from './constants';
import Pagination from 'components/Pagination/Pagination';
import { queryClient } from 'index';
import { ReactQueryKeys } from 'services/api/reactQueryKeys';
import { Plus, MagnifyingGlass as Search } from '@phosphor-icons/react';
import Icon from 'components/Icon/Icon';
import { COLORS } from 'assets/styled';
import { useDebounce } from 'utils/hooks/useDebounce';
import {
  useDeleteModelCompany,
  useGetCompaniesForModel,
  useToggleOrderAllExternallyModels,
  useToggleOrderAllInternallyModels,
} from './hooks';
import { IModelCompany } from 'types/Model.types';
import ModelCompanyRow from './ModelCompanyRow/ModelCompanyRow';
import ModelCompanyRowSkeleton from './ModelCompanyRowSkeleton/ModelCompanyRowSkeleton';
import { AddCompanyToModelModal } from 'components/Modals/AddCompanyToModelModal/AddCompanyToModelModal';
import { DangerModal } from 'components/Modals/DangerModal/DangerModal';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';

interface IModelsCompaniesProps {
  modelId: number;
  companyId: number;
  isPublicModel: boolean;
}

const ModelsCompanies = ({
  modelId,
  companyId,
  isPublicModel,
}: IModelsCompaniesProps) => {
  const { t } = useTranslation();
  const [searchBy, setSearchBy] = useState<string>('');
  const debouncedSearchBy = useDebounce(searchBy);
  const [page, setPage] = useState<number>(1);
  const [isDeleteModelCompanyModalOpen, setIsDeleteModelCompanyModalOpen] =
    useState<boolean>(false);
  const [modelCompanyIdForDeletion, setModelCompanyIdForDeletion] = useState<
    number | null
  >(null);
  const [isAddModelCompanyModalOpen, setIsAddModelCompanyModalOpen] =
    useState<boolean>(false);

  useEffect(() => {
    if (debouncedSearchBy && page !== 1) {
      setPage(1);
    }
  }, [debouncedSearchBy]);

  const { data: modelsCompaniesData, isLoading: isLoadingCompaniesForModel } =
    useGetCompaniesForModel(
      page,
      MODELS_COMPANIES_PER_PAGE,
      debouncedSearchBy,
      modelId
    );

  const modelsCompanies: IModelCompany[] = modelsCompaniesData?.data || [];

  const showPagination =
    modelsCompaniesData?.pagination?.total &&
    modelsCompaniesData?.pagination?.total >= MODELS_COMPANIES_PER_PAGE;

  const {
    mutate: toggleOrderAllInternallyModels,
    isLoading: isLoadingToggleOrderAllInternallyModels,
    isSuccess: isSuccessToggleOrderAllInternallyModels,
    reset: resetToggleOrderAllInternallyModels,
  } = useToggleOrderAllInternallyModels();

  const {
    mutate: toggleOrderAllExternallyModels,
    isLoading: isLoadingToggleOrderAllExternallyModels,
    isSuccess: isSuccessToggleOrderAllExternallyModels,
    reset: resetToggleOrderAllExternallyModels,
  } = useToggleOrderAllExternallyModels();

  useEffect(() => {
    if (
      isSuccessToggleOrderAllInternallyModels ||
      isSuccessToggleOrderAllExternallyModels
    ) {
      queryClient.invalidateQueries([ReactQueryKeys.GET_COMPANIES_FOR_MODEL]);
      resetToggleOrderAllInternallyModels();
      resetToggleOrderAllExternallyModels();
    }
  }, [
    isSuccessToggleOrderAllInternallyModels,
    isSuccessToggleOrderAllExternallyModels,
  ]);

  const isLoading =
    isLoadingCompaniesForModel ||
    isLoadingToggleOrderAllInternallyModels ||
    isLoadingToggleOrderAllExternallyModels;

  const { mutate: deleteModelCompany, isLoading: isLoadingDeleteModelCompany } =
    useDeleteModelCompany();

  return (
    <Container>
      <FirstRow>
        <RowWrapper>
          <LeftSide>
            <CompaniesLabel>{t('Companies')}</CompaniesLabel>
            <InputWrapper>
              <Input
                placeholder={t('Search')}
                width={'100%'}
                wrapperStyles={{ width: '100%' }}
                onChange={(e: any) => setSearchBy(e.target.value)}
                icon={Search}
              />
            </InputWrapper>
          </LeftSide>

          <RightSide>
            <ToggleGroupWrapper>
              <ToggleLabel>{t('Toggle all Internal Ordering')}</ToggleLabel>
              <ToggleSwitch
                disabled={isLoading}
                isChecked={
                  modelsCompaniesData?.ordering_summary
                    ?.can_order_all_internally ?? 'native'
                }
                onClick={() => {
                  if (isLoading) {
                    return;
                  }
                  if (
                    modelsCompaniesData?.ordering_summary
                      ?.can_order_all_internally
                  ) {
                    toggleOrderAllInternallyModels({
                      modelId,
                      companyId,
                      canOrderInternally: false,
                    });
                  } else {
                    toggleOrderAllInternallyModels({
                      modelId,
                      companyId,
                      canOrderInternally: true,
                    });
                  }
                }}
                size={'large'}
              />
            </ToggleGroupWrapper>

            <ToggleGroupWrapper>
              <ToggleLabel>{t('Toggle all External Ordering')}</ToggleLabel>
              <ToggleSwitch
                disabled={isLoading}
                isChecked={
                  modelsCompaniesData?.ordering_summary
                    ?.can_order_all_externally ?? 'native'
                }
                onClick={() => {
                  if (isLoading) {
                    return;
                  }
                  if (
                    modelsCompaniesData?.ordering_summary
                      ?.can_order_all_externally
                  ) {
                    toggleOrderAllExternallyModels({
                      modelId,
                      companyId,
                      canOrderExternally: false,
                    });
                  } else {
                    toggleOrderAllExternallyModels({
                      modelId,
                      companyId,
                      canOrderExternally: true,
                    });
                  }
                }}
                size={'large'}
              />
            </ToggleGroupWrapper>
            <AddWrapper onClick={() => setIsAddModelCompanyModalOpen(true)}>
              <Icon svg={Plus} color={COLORS.GREEN} size={20} />
              <AddLabel>{t('Add Company')}</AddLabel>
            </AddWrapper>
          </RightSide>
        </RowWrapper>
      </FirstRow>

      {modelsCompanies.length ? (
        modelsCompanies.map((modelCompany: IModelCompany, index: number) => (
          <ModelCompanyRow
            key={`company-models-row-${index}`}
            modelCompany={modelCompany}
            withoutLine={modelsCompanies.length - 1 === index}
            isLoadingParent={isLoading}
            onDelete={(modelCompanyId: number) => {
              setIsDeleteModelCompanyModalOpen(true);
              setModelCompanyIdForDeletion(modelCompanyId);
            }}
            isPublic={isPublicModel}
          />
        ))
      ) : isLoadingCompaniesForModel ? (
        Array.from({ length: MODELS_COMPANIES_PER_PAGE }, (_, index) => (
          <ModelCompanyRowSkeleton
            key={index}
            index={index}
            withoutLine={MODELS_COMPANIES_PER_PAGE - 1 === index}
          />
        ))
      ) : (
        <NoContentLabelWrapper>
          <NoContentLabel>
            {t('No companies found for this model')}
          </NoContentLabel>
        </NoContentLabelWrapper>
      )}
      {showPagination ? (
        <Pagination
          page={page}
          perPage={MODELS_COMPANIES_PER_PAGE}
          total={modelsCompaniesData?.pagination?.total}
          setPage={setPage}
        />
      ) : null}
      {isAddModelCompanyModalOpen && (
        <AddCompanyToModelModal
          isOpen={isAddModelCompanyModalOpen}
          setIsOpen={setIsAddModelCompanyModalOpen}
          companyId={companyId}
          modelId={modelId}
        />
      )}
      <DangerModal
        isOpen={isDeleteModelCompanyModalOpen}
        setIsOpen={setIsDeleteModelCompanyModalOpen}
        title={t('Confirm removal')}
        submit={{
          onClick: () => {
            deleteModelCompany(modelCompanyIdForDeletion!);
            setIsDeleteModelCompanyModalOpen(false);
            setModelCompanyIdForDeletion(null);
          },
          text: t('Remove'),
          disabled: isLoadingDeleteModelCompany,
        }}
        cancel={{
          onClick: () => {
            setIsDeleteModelCompanyModalOpen(false);
            setModelCompanyIdForDeletion(null);
          },
        }}
        description={t(
          'Are you sure you want to remove access of this Model for this Company?'
        )}
      />
    </Container>
  );
};

export default ModelsCompanies;
